import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { apiClient } from '../api/client';
import { STAFF_ROLES, type StaffRole } from '../constants/staff';
import { useAuth } from '../auth/AuthContext';
import { useAppSnackbar } from '../snackbar/useAppSnackbar';
import type { StaffListItem } from './StaffList';

/** switch: Google からの方式変更。resend: 招待メール再送 */
type AuthAction = 'switch' | 'resend';

const StaffEdit: React.FC = () => {
  const { id: idParam } = useParams();
  const id = idParam ? Number.parseInt(idParam, 10) : NaN;
  const navigate = useNavigate();
  const { staffProfile } = useAuth();
  const { showSnackbar } = useAppSnackbar();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState('');
  const [authMethodLabel, setAuthMethodLabel] = useState('');
  const [staffRow, setStaffRow] = useState<StaffListItem | null>(null);
  const [authAction, setAuthAction] = useState<AuthAction | null>(null);
  const [authSubmitting, setAuthSubmitting] = useState(false);
  const [form, setForm] = useState({
    display_name: '',
    role: '看護師' as StaffRole,
    is_developer: false,
  });

  const load = useCallback(async () => {
    if (!Number.isInteger(id) || id < 1) {
      setError('無効な職員 ID です');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.get<StaffListItem[]>('/api/staff');
      const row = res.data.find((r) => r.id === id);
      if (!row) {
        setError('職員が見つかりません');
        return;
      }
      setEmail(row.email);
      setAuthMethodLabel(row.auth_method === 'email_password' ? 'それ以外（メール＋パスワード）' : 'Google');
      setStaffRow(row);
      setForm({
        display_name: row.display_name,
        role: row.role,
        is_developer: row.is_developer,
      });
    } catch {
      setError('職員情報の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSubmit = async () => {
    if (!form.display_name.trim()) {
      setError('名前を入力してください');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await apiClient.patch<StaffListItem>(`/api/staff/${id}`, {
        display_name: form.display_name.trim(),
        role: form.role,
        is_developer: form.is_developer,
      });
      navigate('/staff');
    } catch (e: unknown) {
      if (axios.isAxiosError(e) && e.response?.data?.error) {
        setError(String(e.response.data.error));
      } else {
        setError('更新に失敗しました');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmAuthAction = async () => {
    if (!authAction) return;
    const action = authAction;
    setError('');
    setAuthSubmitting(true);
    try {
      await apiClient.patch<StaffListItem>(`/api/staff/${id}`, { auth_method: 'email_password' });
      setAuthAction(null);
      showSnackbar({
        message: action === 'switch' ? 'ログイン方式を変更し、招待メールを送信しました' : '招待メールを再送しました',
        severity: 'success',
      });
      await load();
    } catch (e: unknown) {
      setAuthAction(null);
      if (axios.isAxiosError(e) && e.response?.data?.error) {
        setError(String(e.response.data.error));
      } else {
        setError(action === 'switch' ? 'ログイン方式の変更に失敗しました' : '招待メールの再送に失敗しました');
      }
      await load();
    } finally {
      setAuthSubmitting(false);
    }
  };

  const isSelf = staffProfile?.id === id;
  const canSwitchToEmail = Boolean(staffRow?.is_active) && staffRow?.auth_method === 'google';
  const canResendInvite =
    Boolean(staffRow?.is_active) && staffRow?.auth_method === 'email_password' && !staffRow?.cognito_linked;

  if (!Number.isInteger(id) || id < 1) {
    return (
      <Box sx={{ flex: 1, p: 2 }}>
        <Alert severity="error">無効な職員 ID です</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
      <Dialog
        open={Boolean(authAction)}
        onClose={() => !authSubmitting && setAuthAction(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            m: 2,
          },
        }}
      >
        <DialogTitle sx={{ pt: 3, px: 3, pb: 1, typography: 'h6' }}>
          {authAction === 'switch' ? 'ログイン方式を変更しますか？' : '招待メールを再送しますか？'}
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 1, pb: 2 }}>
          {authAction === 'switch' ? (
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
              {form.display_name}（{email}）をメール＋パスワード方式に変更します。
              <br />
              Google ではログインできなくなります。仮パスワード付きの招待メールが届き、初回ログイン時に新しいパスワードを設定します。
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
              {email} 宛てに新しい仮パスワード付きの招待メールを再送します。
              <br />
              以前の仮パスワードは使えなくなります。
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5, flexWrap: 'wrap' }}>
          <Button onClick={() => setAuthAction(null)} disabled={authSubmitting} sx={{ minWidth: 100 }}>
            キャンセル
          </Button>
          <Button
            color={authAction === 'switch' ? 'warning' : 'primary'}
            variant="contained"
            onClick={() => void handleConfirmAuthAction()}
            disabled={authSubmitting}
            sx={{ minWidth: 100 }}
          >
            {authAction === 'switch' ? '変更して招待メールを送る' : '再送する'}
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5">職員を編集</Typography>
        {/* <Button variant="text" onClick={() => navigate('/staff')}>
          一覧に戻る
        </Button> */}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Typography color="text.secondary">読み込み中…</Typography>
      ) : (
        <Paper sx={{ p: 4, maxWidth: 560, mx: 'auto' }}>
          <TextField
            label="メールアドレス"
            fullWidth
            margin="normal"
            value={email}
            InputProps={{ readOnly: true }}
            disabled={true}
            helperText="変更できません（Cognito との紐付けのため）"
          />
          <TextField
            label="アカウント種別"
            fullWidth
            margin="normal"
            value={authMethodLabel}
            InputProps={{ readOnly: true }}
            disabled={true}
            helperText={canSwitchToEmail ? undefined : '職員追加時のみ指定できます'}
          />
          {canSwitchToEmail && (
            <Box sx={{ mb: 1 }}>
              <Tooltip title={isSelf ? '自分自身のログイン方式は変更できません' : ''}>
                <span>
                  <Button
                    color="warning"
                    variant="outlined"
                    size="small"
                    disabled={isSelf || authSubmitting}
                    onClick={() => setAuthAction('switch')}
                  >
                    メール＋パスワード方式に変更
                  </Button>
                </span>
              </Tooltip>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Google アカウントが使えなくなった職員向け。変更すると Google ではログインできなくなり、仮パスワード付きの招待メールが届きます。
              </Typography>
            </Box>
          )}
          {canResendInvite && (
            <Box sx={{ mb: 1 }}>
              <Button
                variant="outlined"
                size="small"
                disabled={authSubmitting}
                onClick={() => setAuthAction('resend')}
              >
                招待メールを再送
              </Button>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                仮パスワードを忘れた・期限切れの場合に、新しい仮パスワード付きの招待メールを再送します。
              </Typography>
            </Box>
          )}
          <TextField
            label="名前"
            fullWidth
            margin="normal"
            required
            value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
          />
          <TextField
            label="役職"
            select
            fullWidth
            margin="normal"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value as StaffRole })}
          >
            {STAFF_ROLES.map((r) => (
              <MenuItem key={r} value={r}>
                {r}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            sx={{ mt: 2, display: 'flex', alignItems: 'flex-start' }}
            control={
              <Switch
                checked={form.is_developer}
                onChange={(_, v) => setForm({ ...form, is_developer: v })}
              />
            }
            label={
              <Box>
                <Typography variant="body2">開発者アカウント</Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  職員管理以外の院内データをサーバー側でマスクします
                </Typography>
              </Box>
            }
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 1 }}>
            <Button onClick={() => navigate('/staff')} disabled={submitting}>
              キャンセル
            </Button>
            <Button variant="contained" onClick={() => void handleSubmit()} disabled={submitting}>
              保存
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default StaffEdit;
